import React, { useEffect, useState } from "react";
import { Search, Users, Sparkles, FileText, LayoutDashboard, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

export function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data || []);
      } catch {}
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;

  const quickLinks = [
    { label: "Overview", path: "/", icon: LayoutDashboard },
    { label: "Leads", path: "/leads", icon: Sparkles },
    { label: "Customers", path: "/customers", icon: Users },
    { label: "Invoices", path: "/invoices", icon: FileText },
    { label: "Apps", path: "/apps", icon: Smartphone },
  ];

  const iconFor = (t) => {
    if (t === "customer") return Users;
    if (t === "lead") return Sparkles;
    if (t === "invoice") return FileText;
    if (t === "app") return Smartphone;
    return Search;
  };

  const go = (type, id) => {
    const path = {
      customer: `/customers/${id}`,
      lead: `/leads`,
      invoice: `/invoices`,
      app: `/apps`,
    }[type] || "/";
    onOpenChange(false);
    setQ("");
    nav(path);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[16vh] backdrop-blur-md bg-black/30"
      onClick={() => onOpenChange(false)}
      data-testid="command-palette-backdrop"
    >
      <div
        className="w-[min(640px,92vw)] rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--border))]">
          <Search className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers, leads, invoices, apps…"
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[hsl(var(--text-subtle))]"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]">ESC</kbd>
        </div>
        <div className="max-h-[360px] overflow-auto">
          {!q && (
            <div className="p-2">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[hsl(var(--text-subtle))]">Quick links</div>
              {quickLinks.map((l) => (
                <button
                  key={l.path}
                  onClick={() => { onOpenChange(false); nav(l.path); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--surface-hover))] text-left"
                >
                  <l.icon className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
                  <span>{l.label}</span>
                </button>
              ))}
            </div>
          )}
          {q && results.length === 0 && (
            <div className="p-10 text-center text-sm text-[hsl(var(--text-muted))]">No matches for "{q}"</div>
          )}
          {q && results.length > 0 && (
            <div className="p-2">
              {results.map((r, i) => {
                const I = iconFor(r.type);
                return (
                  <button
                    key={r.id + i}
                    onClick={() => go(r.type, r.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--surface-hover))] text-left"
                    data-testid={`search-result-${r.type}`}
                  >
                    <I className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="text-sm">{r.label}</div>
                      <div className="text-xs text-[hsl(var(--text-muted))]">{r.sub}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-subtle))]">{r.type}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
